import clsx from 'clsx'
import type { TableRowProps } from './types'
import type { TableRow as TableRowType } from '../../types'
import { TableCell } from '../Cell'
import { flexRender } from '@tanstack/react-table'
import { useCallback } from 'react'
import Link from 'next/link'

export function TableRow<T>({
  row,
  isCondensed,
  isStriped,
  isLast,
  onRowClick,
}: TableRowProps<T & TableRowType>) {
  const isRowClickable =
    typeof onRowClick === 'function' && !row.original?.['disableRowClick']
  const isRowNavigable =
    !!row.original?.['href'] &&
    typeof row.original['href'] === 'string' &&
    !row.original?.['disableRowClick']
  const handleRowClick = useCallback(() => {
    if (isRowClickable) {
      onRowClick(row.original as T & TableRowType)
    }
  }, [onRowClick, row.original, isRowClickable])

  const getRowContent = () => (
    <tr
      className={clsx('text-left border-b border-default group', {
        '!min-h-20 h-20': !isCondensed,
        '!min-h-12 h-12': isCondensed,
        '!bg-white': !row.original?.['isRowFaded'] && !isStriped,
        '!bg-medium': !row.original?.['isRowFaded'] && isStriped,
        '!bg-default': row.original?.['isRowFaded'],
        'hover:cursor-pointer': isRowClickable || isRowNavigable,
        '!border-0': isLast,
      })}
      onClick={handleRowClick}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          cell={cell.getContext()}
          isCondensed={isCondensed}
          isRowClickable={isRowClickable || isRowNavigable}
          key={cell.id}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </tr>
  )

  return isRowNavigable ? (
    <Link
      className="contents" // "display: contents" is required so the tr child is still seen as a direct child of the tbody parent
      href={row.original['href'] as string}
    >
      {getRowContent()}
    </Link>
  ) : (
    getRowContent()
  )
}
